import type { Plan, BillingInterval, PricingBreakdown, ChangePreview } from "../../../shared/types.js";

export function previewMatchesSelection(
  preview: ChangePreview | null,
  plan: Plan,
  interval: BillingInterval,
): boolean {
  return !!preview && preview.targetPlan === plan && preview.targetInterval === interval;
}

export function isScheduledDecision(decision: string): boolean {
  return (
    decision === "schedule_downgrade_plan" ||
    decision === "schedule_downgrade_interval" ||
    decision === "schedule_payment_method_change"
  );
}

/**
 * Computes "A pagar agora" for the integrated plan-change UI.
 * Returns null while a new selection is waiting for a matching pricing response
 * (avoids flashing R$ 0,00 from stale no_op / scheduled previews).
 */
export function computeIntegratedDueTodayCents(args: {
  selectionUnchanged: boolean;
  /** Expired customers can reactivate the same plan — still due today. */
  subscriptionExpired?: boolean;
  /** Offer 1-click Premium: no new checkout, nothing due today. */
  canRaiseInPlace?: boolean;
  preview: ChangePreview | null;
  pricing: PricingBreakdown | null;
  plan: Plan;
  interval: BillingInterval;
}): number | null {
  if (args.selectionUnchanged && !args.subscriptionExpired) return 0;
  if (args.canRaiseInPlace) return 0;

  const matches = previewMatchesSelection(args.preview, args.plan, args.interval);

  if (matches && args.preview) {
    if (args.preview.decision === "blocked_annual_to_monthly_pix") return null;
    if (args.preview.decision === "blocked_pending_scheduled_change") {
      if (args.preview.chargeNowCents > 0) {
        return args.pricing?.finalCents ?? args.preview.chargeNowCents;
      }
      return 0;
    }
    if (isScheduledDecision(args.preview.decision)) return 0;
    if (args.preview.decision === "immediate_upgrade") {
      return args.pricing?.finalCents ?? args.preview.chargeNowCents;
    }
  }

  if (!matches) {
    if (args.pricing?.finalCents != null && args.pricing.finalCents > 0) {
      return args.pricing.finalCents;
    }
    return null;
  }

  return args.pricing?.finalCents ?? 0;
}
